import { Button, Input, Tooltip } from 'antd';
import { Check, Loader2, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { slugify } from './slugify';

interface SlugInputProps {
  value: string;
  onChange: (next: string) => void;
  sourceValue?: string;
  onCheckUnique?: (slug: string) => Promise<boolean>;
  placeholder?: string;
  prefix?: string;
  disabled?: boolean;
}

type UniqueState = 'idle' | 'checking' | 'ok' | 'taken';

export function SlugInput({
  value,
  onChange,
  sourceValue,
  onCheckUnique,
  placeholder,
  prefix,
  disabled,
}: SlugInputProps) {
  const { t } = useTranslation('components');
  const [manual, setManual] = useState(!!value);
  const [unique, setUnique] = useState<UniqueState>('idle');
  const debounceRef = useRef<number | null>(null);
  const lastChecked = useRef('');

  useEffect(() => {
    if (!manual && sourceValue !== undefined) {
      const next = slugify(sourceValue);
      if (next !== value) onChange(next);
    }
  }, [sourceValue, manual, onChange, value]);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    let cancelled = false;
    if (!onCheckUnique || !value) {
      debounceRef.current = window.setTimeout(() => {
        if (!cancelled) setUnique('idle');
      }, 0);
      return () => {
        cancelled = true;
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
      };
    }
    debounceRef.current = window.setTimeout(() => {
      if (cancelled) return;
      setUnique('checking');
      lastChecked.current = value;
      void onCheckUnique(value)
        .then((ok) => {
          if (!cancelled) setUnique(ok ? 'ok' : 'taken');
        })
        .catch(() => {
          if (!cancelled) setUnique('idle');
        });
    }, 400);
    return () => {
      cancelled = true;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, onCheckUnique]);

  const regenerate = useCallback(() => {
    if (sourceValue === undefined) return;
    setManual(false);
    onChange(slugify(sourceValue));
  }, [sourceValue, onChange]);

  const suffix = (
    <span className="ds-slug__status" aria-live="polite">
      {unique === 'checking' ? <Loader2 size={14} className="ds-spin" /> : null}
      {unique === 'ok' ? <Check size={14} style={{ color: 'var(--ds-success)' }} /> : null}
      {unique === 'taken' ? <X size={14} style={{ color: 'var(--ds-danger)' }} /> : null}
      <Tooltip title={t('slugInput.regenerateTooltip')}>
        <Button
          type="text"
          size="small"
          onClick={regenerate}
          icon={<RefreshCw size={14} />}
          aria-label={t('slugInput.regenerate')}
          disabled={disabled || sourceValue === undefined}
        />
      </Tooltip>
    </span>
  );

  return (
    <div className="ds-slug">
      <Input
        prefix={prefix ? <span className="ds-slug__prefix">{prefix}</span> : undefined}
        suffix={suffix}
        value={value}
        onChange={(e) => {
          setManual(true);
          onChange(slugify(e.target.value).replace(/^-+/, ''));
        }}
        placeholder={placeholder ?? t('slugInput.placeholder')}
        disabled={disabled}
        status={unique === 'taken' ? 'error' : undefined}
      />
      {unique === 'taken' ? (
        <div className="ds-slug__hint" role="alert">
          {t('slugInput.taken')}
        </div>
      ) : null}
    </div>
  );
}
